import Service, { service } from '@ember/service';
import { waitFor } from '@ember/test-waiters';
import { tracked } from '@glimmer/tracking';

import { negotiateLanguages } from '@fluent/langneg';
import { shouldPolyfill as shouldPolyfillCanonicalLocales } from '@formatjs/intl-getcanonicallocales/should-polyfill.js';
import { shouldPolyfill as shouldPolyfillIntlLocale } from '@formatjs/intl-locale/should-polyfill.js';
import { shouldPolyfill as shouldPolyfillPluralRules } from '@formatjs/intl-pluralrules/should-polyfill.js';
import {
  DEFAULT_LOCALE,
  LOCALE_CODES,
  type LocaleCode,
  LOCALES,
} from '@repo/shared-config/constants/locales';
import { restartableTask } from 'ember-concurrency';
import type IntlService from 'ember-intl/services/intl';

type SupportedLocale = LocaleCode;

async function polyfillPluralRules(locale: SupportedLocale): Promise<void> {
  if (shouldPolyfillIntlLocale()) {
    await import('@formatjs/intl-locale/polyfill.js');
  }
  if (shouldPolyfillPluralRules()) {
    await import('@formatjs/intl-pluralrules/polyfill.js');
  }

  // @ts-expect-error -- Boolean property `polyfilled` does not exist on the `Intl.PluralRulesConstructor` type, as it is added by the formatjs polyfill
  if (Intl.PluralRules.polyfilled) {
    switch (locale) {
      case 'de':
        await import('@formatjs/intl-pluralrules/locale-data/de.js');
        break;
      case 'en':
        await import('@formatjs/intl-pluralrules/locale-data/en.js');
        break;
      case 'it':
        await import('@formatjs/intl-pluralrules/locale-data/it.js');
        break;
      case 'fr':
        await import('@formatjs/intl-pluralrules/locale-data/fr.js');
        break;
      case 'es':
        await import('@formatjs/intl-pluralrules/locale-data/es.js');
        break;
      case 'pt':
        await import('@formatjs/intl-pluralrules/locale-data/pt.js');
        break;
      case 'nl':
        await import('@formatjs/intl-pluralrules/locale-data/nl.js');
        break;
    }
  }
}

async function getCanonicalLocalesPolyfill(): Promise<void> {
  if (shouldPolyfillCanonicalLocales()) {
    await import('@formatjs/intl-getcanonicallocales/polyfill.js');
  }
}

/**
 * Service responsible for managing application locale and locale switching.
 * Handles polyfill loading, translation loading, and coordination with other services.
 */
export default class LocaleManager extends Service {
  @service declare intl: IntlService;
  // @ts-expect-error -- zendeskWidget is not typed, yet ;)
  @service zendeskWidget;

  @tracked locale?: SupportedLocale;

  getLocales = LOCALES;
  getLocaleCodes = LOCALE_CODES;

  negotiateLocale(userLocale: string = DEFAULT_LOCALE): SupportedLocale {
    let [negotiatedLocale] = negotiateLanguages([userLocale], this.getLocaleCodes, {
      strategy: 'lookup',
      defaultLocale: DEFAULT_LOCALE,
    });

    // oxlint-disable-next-line typescript/consistent-type-assertions
    return negotiatedLocale as SupportedLocale;
  }

  loadAndSetLocaleTask = restartableTask(
    waitFor(async (locale: SupportedLocale) => {
      try {
        // Load the polyfill first, before loading the translations
        await Promise.all([polyfillPluralRules(locale), getCanonicalLocalesPolyfill()]);
      } catch {
        // No benefit in logging network errors
      }

      // oxlint-disable-next-line typescript/no-unsafe-assignment
      let translations = await import(`/translations/${locale}.json`);
      // oxlint-disable-next-line typescript/no-unsafe-argument
      this.intl.addTranslations(locale, translations);

      this.intl.setLocale(locale);
      this.locale = locale;
      // oxlint-disable-next-line typescript/no-unsafe-member-access
      this.zendeskWidget.localeChange(locale);
    })
  );

  loadAndSetLocale(locale: SupportedLocale): Promise<void> {
    return this.loadAndSetLocaleTask.perform(locale);
  }
}

declare module '@ember/service' {
  interface Registry {
    'locale-manager': LocaleManager;
    localeManager: LocaleManager;
  }
}
